import type { TagDto } from '../types/admin.types';
import type { Video } from '../types/video.types';

/**
 * Maximum number of personal-library discovery items shown per group.
 */
export interface LibraryDiscoveryLimits {
    /** Maximum tag chips to show. */
    tags: number;
    /** Maximum fighter rows to show. */
    fighters: number;
    /** Maximum event rows to show. */
    events: number;
}

/**
 * Discovery metadata extracted from a personal library video set.
 */
export interface LibraryDiscoveryMetadata {
    /** Unique tags extracted from the current library video set. */
    tags: Pick<TagDto, 'id' | 'name'>[];
    /** Unique fighter names extracted from the current library video set. */
    fighters: string[];
    /** Unique event names extracted from the current library video set. */
    events: string[];
}

const DEFAULT_DISCOVERY_LIMITS: LibraryDiscoveryLimits = {
    tags: 10,
    fighters: 8,
    events: 6
};

const cleanValue = (value?: string | null) => value?.trim() ?? '';

const normalizeKey = (value: string) => cleanValue(value).toLowerCase();

const shouldUseNamedEntity = (value?: string | null) => {
    const cleaned = cleanValue(value);
    return cleaned.length > 0 && cleaned.toLowerCase() !== 'tbd';
};

const uniqueNamedValues = (values: Array<string | null | undefined>) => {
    const uniqueValues = new Map<string, string>();

    values.forEach((value) => {
        if (!shouldUseNamedEntity(value)) {
            return;
        }

        const cleaned = cleanValue(value);
        const key = normalizeKey(cleaned);
        if (!uniqueValues.has(key)) {
            uniqueValues.set(key, cleaned);
        }
    });

    return Array.from(uniqueValues.values());
};

const addCountedValue = (items: Map<string, { value: string; count: number }>, value?: string | null) => {
    const cleaned = cleanValue(value);
    const key = normalizeKey(cleaned);

    if (!key) {
        return;
    }

    const existing = items.get(key);
    if (existing) {
        existing.count += 1;
        return;
    }

    items.set(key, { value: cleaned, count: 1 });
};

const sortCountedValues = (items: Map<string, { value: string; count: number }>) => (
    Array.from(items.values()).sort((left, right) => {
        if (left.count !== right.count) {
            return right.count - left.count;
        }

        return left.value.localeCompare(right.value);
    })
);

const buildTagId = (tagName: string) => (
    `library-tag-${normalizeKey(tagName).replace(/[^a-z0-9]+/g, '-')}`
);

const getVideoTags = (video: Video) => uniqueNamedValues([
    ...(video.EventTags ?? []),
    ...(video.Tags ?? [])
]);

const getVideoFighters = (video: Video) => uniqueNamedValues([
    video.FighterA,
    video.FighterB,
    ...(video.Participants ?? []).map((participant) => participant.ParticipantName)
]);

const getSearchValues = (video: Video) => uniqueNamedValues([
    video.Title,
    video.EventName,
    video.LocationName,
    video.EventType,
    ...getVideoFighters(video),
    ...getVideoTags(video)
]);

/**
 * Builds unique, frequency-sorted metadata for a set of personal-library videos.
 */
export const buildLibraryDiscoveryMetadata = (
    videos: Video[],
    limits: LibraryDiscoveryLimits = DEFAULT_DISCOVERY_LIMITS
): LibraryDiscoveryMetadata => {
    const tags = new Map<string, { value: string; count: number }>();
    const fighters = new Map<string, { value: string; count: number }>();
    const events = new Map<string, { value: string; count: number }>();

    videos.forEach((video) => {
        getVideoTags(video).forEach((tag) => addCountedValue(tags, tag));
        getVideoFighters(video).forEach((fighter) => addCountedValue(fighters, fighter));
        addCountedValue(events, video.EventName);
    });

    return {
        tags: sortCountedValues(tags).slice(0, limits.tags).map((tag) => ({
            id: buildTagId(tag.value),
            name: tag.value
        })),
        fighters: sortCountedValues(fighters).slice(0, limits.fighters).map((fighter) => fighter.value),
        events: sortCountedValues(events).slice(0, limits.events).map((event) => event.value)
    };
};

/**
 * Filters personal-library videos by title, fighter, event, location, or tag.
 */
export const filterLibraryVideos = (videos: Video[], query: string) => {
    const normalizedQuery = normalizeKey(query);
    const tagOnlyQuery = normalizedQuery.startsWith('#');
    const needle = tagOnlyQuery ? normalizedQuery.slice(1).trim() : normalizedQuery;

    if (!needle) {
        return videos;
    }

    return videos.filter((video) => {
        if (tagOnlyQuery) {
            return getVideoTags(video).some((tag) => normalizeKey(tag).includes(needle));
        }

        return getSearchValues(video).some((value) => normalizeKey(value).includes(needle));
    });
};
